import type { ReactNode } from 'react'
import { useId } from 'react'

/**
 * Label + control + error message, wired together for screen readers.
 *
 * The id is generated here and handed back through a render prop, so a caller cannot
 * forget to connect the label to its input -- the failure mode that makes a form
 * unusable with a screen reader while looking perfectly fine.
 */
export function Field({
  label,
  error,
  hint,
  action,
  children,
}: {
  label: string
  error?: string
  hint?: string
  /** Sits on the label's own line, hard right -- a "Forgot password?" link and little else. */
  action?: ReactNode
  children: (props: {
    id: string
    'aria-invalid': boolean
    'aria-describedby': string | undefined
  }) => ReactNode
}) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const describedBy = error ? errorId : hint ? hintId : undefined

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={id}
          className="block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {label}
        </label>
        {action}
      </div>

      {children({ id, 'aria-invalid': Boolean(error), 'aria-describedby': describedBy })}

      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
